import React from "react";
import { motion } from "framer-motion";
import {
  BriefcaseBusiness,
  CalendarDays,
  MapPin,
  Code2,
  CheckCircle2,
  Sparkles,
  Database,
} from "lucide-react";

const experiences = [
  {
    company: "TechPulse IT Services",
    role: "Java Backend Developer",
    type: "Professional Experience",
    duration: "September 2025 – Present",
    location: "India",
    current: true,

    description:
      "Working on enterprise backend applications using Java and Spring Boot, with a strong focus on microservices, database integration, messaging, reporting, and XML-based system integrations.",

    responsibilities: [
      "Developing backend services and microservices using Java and Spring Boot.",
      "Designing and implementing REST APIs for enterprise application requirements.",
      "Working with MyBatis for SQL-based database operations and data mapping.",
      "Working with SQL Server for database queries, procedures, and data management.",
      "Implementing asynchronous communication and message processing using ActiveMQ.",
      "Working with XML-based request, response, and data mapping configurations.",
      "Developing and maintaining JasperReports for application reporting requirements.",
      "Debugging, enhancing, and maintaining existing backend services and integrations.",
    ],

    technologies: [
      "Java",
      "Spring Boot",
      "Microservices",
      "MyBatis",
      "SQL Server",
      "ActiveMQ",
      "JasperReports",
      "XML Mapper",
      "REST API",
    ],
  },

  {
    company: "Hefshine Software Pvt. Ltd.",
    role: "Java / Full Stack Developer Intern",
    type: "Internship",
    duration: "May 14, 2024 – May 25, 2025",
    location: "India",
    current: false,

    description:
      "Started my professional development journey by working with Java, Spring Boot, MySQL, and React while gaining hands-on experience building real-world applications.",

    responsibilities: [
      "Developed backend functionality using Java and Spring Boot.",
      "Created and tested REST APIs for application features.",
      "Worked with MySQL and JPA/Hibernate for database operations.",
      "Built frontend functionality using React.",
      "Used Git, Maven, and Postman during application development.",
    ],

    technologies: [
      "Java",
      "Spring Boot",
      "React",
      "MySQL",
      "JPA",
      "Hibernate",
      "Maven",
      "Postman",
    ],
  },
];

const stats = [
  {
    value: "~2",
    label: "Years Experience",
    icon: BriefcaseBusiness,
  },
  {
    value: "Microservices",
    label: "Backend Architecture",
    icon: Sparkles,
  },
  {
    value: "Java",
    label: "Primary Technology",
    icon: Code2,
  },
];

const Experience = () => {
  return (
    <section
      id="experience"
      className="relative overflow-hidden bg-[#08080c] py-24 text-white sm:py-28"
    >
      {/* =========================================================
          BACKGROUND
      ========================================================= */}
      <div className="pointer-events-none absolute inset-0">
        {/* Orange Glow */}
        <div className="absolute left-[8%] top-[12%] h-[350px] w-[350px] rounded-full bg-orange-500/[0.035] blur-[120px]" />

        {/* Purple Glow */}
        <div className="absolute bottom-[10%] right-[8%] h-[400px] w-[400px] rounded-full bg-purple-500/[0.025] blur-[130px]" />

        {/* Grid */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-10">
        {/* =========================================================
            SECTION HEADER
        ========================================================= */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="mx-auto max-w-3xl text-center"
        >
          {/* Badge */}
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-orange-500/20 bg-orange-500/[0.06] px-4 py-2">
            <BriefcaseBusiness size={14} className="text-orange-400" />

            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-300">
              Experience
            </span>
          </div>

          {/* Heading */}
          <h2 className="text-4xl font-black tracking-[-0.035em] sm:text-5xl lg:text-6xl">
            My Professional
            <span className="block bg-gradient-to-r from-orange-400 via-orange-500 to-amber-400 bg-clip-text text-transparent">
              Journey.
            </span>
          </h2>

          {/* Description */}
          <p className="mx-auto mt-6 max-w-2xl text-sm leading-7 text-gray-500 sm:text-base">
            From starting my journey as a developer intern to working on
            enterprise backend systems, each experience has helped me grow as a
            Java backend engineer.
          </p>
        </motion.div>

        {/* =========================================================
            EXPERIENCE STATS
        ========================================================= */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mx-auto mt-12 grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3"
        >
          {stats.map((stat) => {
            const Icon = stat.icon;

            return (
              <motion.div
                key={stat.label}
                whileHover={{ y: -4 }}
                transition={{ duration: 0.25 }}
                className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-5 text-center backdrop-blur-xl"
              >
                <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl border border-orange-500/15 bg-orange-500/[0.07] text-orange-400">
                  <Icon size={18} />
                </div>

                <p className="mt-3 text-2xl font-black text-gray-100">
                  {stat.value}
                </p>

                <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-gray-600">
                  {stat.label}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* =========================================================
            EXPERIENCE TIMELINE
        ========================================================= */}
        <div className="relative mx-auto mt-20 max-w-5xl">
          {/* Timeline Line */}
          <div className="absolute left-[19px] top-2 hidden h-[calc(100%-8px)] w-px bg-gradient-to-b from-orange-500/40 via-orange-500/15 to-transparent md:block" />

          <div className="space-y-10">
            {experiences.map((experience, index) => (
              <motion.article
                key={experience.company}
                initial={{
                  opacity: 0,
                  y: 35,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                  amount: 0.15,
                }}
                transition={{
                  duration: 0.7,
                  delay: index * 0.15,
                }}
                className="relative md:pl-16"
              >
                {/* =================================================
                    TIMELINE DOT
                ================================================= */}
                <div className="absolute left-0 top-8 hidden md:flex">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full border ${
                      experience.current
                        ? "border-orange-400/40 bg-orange-500/10 shadow-[0_0_25px_rgba(249,115,22,0.15)]"
                        : "border-white/10 bg-[#111116]"
                    }`}
                  >
                    <div
                      className={`h-2.5 w-2.5 rounded-full ${
                        experience.current
                          ? "bg-orange-400 shadow-[0_0_10px_rgba(249,115,22,0.8)]"
                          : "bg-gray-600"
                      }`}
                    />
                  </div>
                </div>

                {/* =================================================
                    EXPERIENCE CARD
                ================================================= */}
                <div
                  className={`group relative overflow-hidden rounded-3xl border p-6 backdrop-blur-xl transition-all duration-500 sm:p-8 ${
                    experience.current
                      ? "border-orange-500/15 bg-gradient-to-br from-orange-500/[0.055] to-white/[0.02] hover:border-orange-500/25"
                      : "border-white/[0.07] bg-white/[0.025] hover:border-white/[0.12]"
                  }`}
                >
                  {/* Current Job Glow */}
                  {experience.current && (
                    <div className="pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-orange-500/[0.06] blur-[70px]" />
                  )}

                  {/* =================================================
                      TOP SECTION
                  ================================================= */}
                  <div className="relative flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      {/* Role + Current */}
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-xl font-bold text-gray-100 sm:text-2xl">
                          {experience.role}
                        </h3>

                        {experience.current && (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/15 bg-emerald-500/[0.07] px-2.5 py-1 text-[9px] font-semibold uppercase tracking-wider text-emerald-400">
                            <span className="relative flex h-1.5 w-1.5">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />

                              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                            </span>
                            Current
                          </span>
                        )}
                      </div>

                      {/* Company */}
                      <p className="mt-2 text-base font-semibold text-orange-400">
                        {experience.company}
                      </p>
                    </div>

                    {/* Experience Type */}
                    <span className="w-fit rounded-xl border border-white/[0.07] bg-white/[0.025] px-3 py-2 text-xs font-medium text-gray-500">
                      {experience.type}
                    </span>
                  </div>

                  {/* =================================================
                      META INFORMATION
                  ================================================= */}
                  <div className="relative mt-5 flex flex-wrap gap-x-5 gap-y-2 border-y border-white/[0.05] py-4">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <CalendarDays size={14} className="text-orange-400/70" />

                      {experience.duration}
                    </div>

                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <MapPin size={14} className="text-orange-400/70" />

                      {experience.location}
                    </div>
                  </div>

                  {/* =================================================
                      DESCRIPTION
                  ================================================= */}
                  <p className="relative mt-5 text-sm leading-7 text-gray-400">
                    {experience.description}
                  </p>

                  {/* =================================================
                      KEY CONTRIBUTIONS
                  ================================================= */}
                  <div className="relative mt-7">
                    <p className="mb-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-600">
                      Key Contributions
                    </p>

                    <div className="space-y-3">
                      {experience.responsibilities.map((item) => (
                        <div
                          key={item}
                          className="flex items-start gap-3 text-sm leading-6 text-gray-400"
                        >
                          <CheckCircle2
                            size={15}
                            className="mt-1 shrink-0 text-orange-400/70"
                          />

                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* =================================================
                      TECHNOLOGIES
                  ================================================= */}
                  <div className="relative mt-7">
                    <div className="mb-3 flex items-center gap-2">
                      <Code2 size={14} className="text-orange-400" />

                      <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-600">
                        Technologies
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {experience.technologies.map((technology) => (
                        <span
                          key={technology}
                          className="rounded-lg border border-white/[0.07] bg-white/[0.025] px-2.5 py-1.5 text-[10px] font-medium text-gray-400 transition-colors duration-300 group-hover:border-orange-500/10 group-hover:text-orange-300"
                        >
                          {technology}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>

        {/* =========================================================
            CURRENT FOCUS
        ========================================================= */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mx-auto mt-16 max-w-3xl"
        >
          <div className="relative overflow-hidden rounded-3xl border border-orange-500/10 bg-orange-500/[0.025] p-6 text-center backdrop-blur-xl sm:p-8">
            {/* Glow */}
            <div className="pointer-events-none absolute left-1/2 top-0 h-32 w-64 -translate-x-1/2 rounded-full bg-orange-500/[0.06] blur-[70px]" />

            <div className="relative">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-orange-500/15 bg-orange-500/[0.07] text-orange-400">
                <Database size={20} />
              </div>

              <h3 className="mt-4 text-lg font-bold text-gray-200">
                Focused on Backend Engineering
              </h3>

              <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-gray-500">
                Building scalable services, reliable APIs, database-driven
                applications, messaging workflows, and enterprise backend
                solutions.
              </p>

              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {[
                  "Spring Boot",
                  "Microservices",
                  "MyBatis",
                  "SQL Server",
                  "ActiveMQ",
                ].map((technology) => (
                  <span
                    key={technology}
                    className="rounded-lg border border-orange-500/10 bg-orange-500/[0.04] px-3 py-1.5 text-[10px] font-medium text-orange-300"
                  >
                    {technology}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* =========================================================
            BOTTOM STATEMENT
        ========================================================= */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/[0.07] bg-white/[0.025] px-4 py-2 text-xs text-gray-500">
            <Sparkles size={13} className="text-orange-400" />
            Always learning. Always building.
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;
